import React from "react";
import ReportBreakModal, { ReportBreakConfig } from "./ReportBreakModal";

const CONFIG_SELECTOR = "[data-breaks-report-config]";

/** Läser gränsvärdena ur det som Razor renderat på klassidan. Nyckeln finns aldrig i html:en. */
function readConfig(token: string): ReportBreakConfig | null {
  const element = document.querySelector<HTMLElement>(CONFIG_SELECTOR);

  if (!element || token === "") return null;

  const maxMinutes = Number.parseInt(
    element?.dataset.reportMaxMinutes ?? "",
    10,
  );
  const maxStudents = Number.parseInt(
    element?.dataset.reportMaxStudents ?? "",
    10,
  );
  const students = Number.parseInt(element?.dataset.reportStudents ?? "", 10);

  return {
    token,
    maxMinutesPerBreak:
      Number.isNaN(maxMinutes) || maxMinutes < 1 ? 120 : maxMinutes,
    maxStudentsPerClass:
      Number.isNaN(maxStudents) || maxStudents < 1 ? 100 : maxStudents,
    registeredStudents: Number.isNaN(students) || students < 0 ? 0 : students,
    className: element?.dataset.reportClass ?? "",
  };
}

/**
 * Håller öppet/stängt-läget för rapporteringsmodalen. Knapparna renderas av Razor på
 * klassidan och märks med data-breaks-report — klicken fångas via delegering på
 * document i stället för att varje knapp får en egen React-rot. Monteras först när
 * API:et godkänt nyckeln, se initBreakReporting.
 */
export default function ReportBreakModalHost({
  token,
}: {
  token: string;
}): React.JSX.Element | null {
  const [config, setConfig] = React.useState<ReportBreakConfig | null>(null);
  const [reported, setReported] = React.useState(false);

  React.useEffect(() => {
    const onClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest("[data-breaks-report]")) return;
      event.preventDefault();
      setConfig(readConfig(token));
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [token]);

  const close = React.useCallback((): void => {
    // Sidans siffror renderas av Razor, så de är inaktuella efter en rapport.
    if (reported) {
      window.location.reload();
      return;
    }

    setConfig(null);
  }, [reported]);

  // Modalen avmonteras vid stängning så att ModalShell hinner städa upp body-klassen
  // och lämna tillbaka fokus till knappen som öppnade den.
  if (!config) return null;

  return (
    <ReportBreakModal
      config={config}
      onClose={close}
      onReported={() => setReported(true)}
    />
  );
}
